import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, MenuItem, Select, TextField } from '@material-ui/core';
import { KeyboardDatePicker, KeyboardTimePicker } from '@material-ui/pickers';
import _ from 'lodash';

import './AddMeetingForm.scss';
import AddMeetingContext from 'src/pages/AddMeeting/Context/AddMeeting.context';

export default function AddMeetingFormStep1() {
  const { stepErrors, setStepErrors, formData } = useContext(AddMeetingContext);
  const { t } = useTranslation('common');

  const [track, setTrack] = useState<{ id: string, name: string }>({ id: '', name: '' });
  const [meeting, setMeeting] = useState('new');
  const [newMeeting, setNewMeeting] = useState('');
  const [meetingDate, setMeetingDate] = useState(null);
  const [numberRaces, setNumberRaces] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [intervalTime, setIntervalTime] = useState('');

  const tracks = [
    { id: 'id1', name: 'name1' },
    { id: 'id2', name: 'name2' }
  ];

  const onTrackChange = (e) => {
    const { value } = e.target;

    const newTrack = _.find(tracks, t => t.id === value.toString() || t.name === value.toString());
    setTrack(newTrack);
  };

  const onError = (element: string, message: string) => {
    if (_.isEmpty(message)) {
      const _stepErrors = [...stepErrors];
      const errorIndex = _.findIndex(_stepErrors, { step: 0, element });
      if (errorIndex !== -1) {
        _.pullAt(_stepErrors, errorIndex);

        setStepErrors(_stepErrors);
      }
    } else {
      const error = _.find(stepErrors, { step: 0, element });
      if (error) {
        error.message = message;
      } else {
        stepErrors.push({ step: 0, element, message });
      }

      setStepErrors(stepErrors);
    }
  };

  useEffect(() => {
    setTrack({ id: formData.trackID || '', name: formData.trackName || '' });
    setMeeting(formData.meeting || 'new');
    setNewMeeting(formData.newMeeting || '');
    setMeetingDate(formData.meetingDate || null);
    setNumberRaces(formData.numberRaces || '');
    setStartTime(formData.startTime || null);
    setIntervalTime(formData.intervalTime || '');
  }, []);

  return (
    <div className="add-meeting-form__step">
      <div className="add-meeting-form__column">
        <div className="add-meeting-form__title">{ t('addMeetingForm.addMeeting') }</div>
        <FormControl variant="outlined" className="add-meeting-form__control">
          <label className="add-meeting-form__label">{ t('addMeetingForm.providerID') }</label>
          <div className="add-meeting-form__provider">1&#39; indicates Xturf provided</div>
        </FormControl>
        <FormControl variant="outlined" className="add-meeting-form__control">
          <label className="add-meeting-form__label">{ t('addMeetingForm.searchByTrackName') }</label>
          <Select
            className="add-meeting-form__input"
            name="trackName"
            value={ track.name }
            onChange={ onTrackChange }
            required
          >
            {
              _.map(tracks, t => (
                <MenuItem key={ t.id } value={ t.name }>{ t.name }</MenuItem>
              ))
            }
          </Select>
        </FormControl>
        <FormControl variant="outlined" className="add-meeting-form__control">
          <label className="add-meeting-form__label">{ t('addMeetingForm.searchByTrackID') }</label>
          <Select
            className="add-meeting-form__input"
            name="trackID"
            value={ track.id }
            onChange={ onTrackChange }
            required
          >
            {
              _.map(tracks, t => (
                <MenuItem key={ t.id } value={ t.id }>{ t.id }</MenuItem>
              ))
            }
          </Select>
        </FormControl>
      </div>
      <div className="add-meeting-form__column">
        <div className="add-meeting-form__title">{ t('addMeetingForm.setUpRaces') }</div>
        <FormControl variant="outlined" className="add-meeting-form__control">
          <label className="add-meeting-form__label">{ t('addMeetingForm.meeting') }</label>
          <Select
            className="add-meeting-form__input"
            name="meeting"
            value={ meeting }
            onChange={ (e) => setMeeting(e.target.value.toString()) }
            required
          >
            <MenuItem value="new">{ t('addMeetingForm.new') }</MenuItem>
            <MenuItem value="name1">name1</MenuItem>
          </Select>
        </FormControl>
        {
          meeting === 'new' && (
            <FormControl variant="outlined" className="add-meeting-form__control">
              <label className="add-meeting-form__label">{ t('addMeetingForm.newMeeting') }</label>
              <TextField
                variant="outlined"
                className="add-meeting-form__input"
                name="newMeeting"
                value={ newMeeting }
                onChange={ (e) => setNewMeeting(e.target.value.toString()) }
                required
              />
            </FormControl>
          )
        }
        <FormControl variant="outlined" className="add-meeting-form__control">
          <label className="add-meeting-form__label">{ t('addMeetingForm.meetingDate') }</label>
          <KeyboardDatePicker
            className="add-meeting-form__input"
            name="meetingDate"
            value={ meetingDate }
            inputValue={ meetingDate }
            onChange={ (date, value) => setMeetingDate(value) }
            placeholder="dd/mm/yy"
            format="dd/MM/yy"
            mask="__/__/__"
            inputVariant="outlined"
            variant="inline"
            orientation="landscape"
            minDateMessage={ t('addMeetingForm.dateInPast') }
            disablePast
            required
            onError={ (error) => onError('meetingDate', error.toString()) }
          />
        </FormControl>
        <FormControl variant="outlined" className="add-meeting-form__control">
          <label className="add-meeting-form__label">{ t('addMeetingForm.numberRaces') }</label>
          <TextField
            variant="outlined"
            className="add-meeting-form__input"
            name="numberRaces"
            type="number"
            value={ numberRaces }
            onChange={ (e) => setNumberRaces(e.target.value.toString()) }
            required
          />
        </FormControl>
        <FormControl variant="outlined" className="add-meeting-form__control">
          <label className="add-meeting-form__label">{ t('addMeetingForm.startTime') }</label>
          <KeyboardTimePicker
            className="add-meeting-form__input"
            name="startTime"
            value={ startTime }
            inputValue={ startTime }
            onChange={ (date, value) => setStartTime(value) }
            placeholder="hh:mm"
            inputVariant="outlined"
            variant="inline"
            orientation="landscape"
            ampm={ false }
            required
            onError={ (error) => onError('startTime', error.toString()) }
          />
        </FormControl>
        <FormControl variant="outlined" className="add-meeting-form__control">
          <label className="add-meeting-form__label">{ t('addMeetingForm.intervalTime') }</label>
          <TextField
            variant="outlined"
            className="add-meeting-form__input"
            name="intervalTime"
            type="number"
            value={ intervalTime }
            onChange={ (e) => setIntervalTime(e.target.value.toString()) }
            required
          />
        </FormControl>
      </div>
    </div>
  );
}
